import { useCallback, useEffect, useRef, useState } from 'react'
import { createMailDraft, updateMailDraft, getMailDraft, MailApiError, DraftContent, MailDraft } from 'services/mail'
import { validMailAddress } from 'components/mail/MailRecipientInput'
export const draftSnapshot = ({ to, cc, bcc, subject, body_text, document_ids, attachment_ids, reply_to, mode }: DraftContent): DraftContent =>
  ({ to: [...to], cc: [...cc], bcc: [...bcc], subject, body_text, document_ids: [...document_ids], attachment_ids: [...attachment_ids], reply_to: reply_to ?? null, mode })
export function draftValidation(content: DraftContent, hasBuffer: boolean): string {
  if (hasBuffer) return 'Conferma i destinatari per salvare'
  const recipients = [...content.to, ...content.cc, ...content.bcc]
  if (!recipients.length) return 'Aggiungi un destinatario valido per salvare'
  if (!recipients.every(validMailAddress)) return 'Correggi i destinatari per salvare'
  if (content.document_ids.length + content.attachment_ids.length > 20) return 'Massimo 20 allegati.'
  if (Array.from(content.subject).length > 998 || Array.from(content.body_text).length > 1000000) return 'Oggetto o testo oltre il limite consentito.'
  return ''
}
interface Options {
  sessionId: string; mailbox: string; content: DraftContent; draft: MailDraft | null
  dirty: boolean; hasBuffer: boolean; permitted: boolean; paused: boolean
  onReload: (draft: MailDraft) => void; onRevoked: () => void
  onSaved: (draft: MailDraft) => void; onClean: () => void
}
export interface DraftRecovery {
  kind: 'conflict' | 'post-unknown' | 'verify' | 'retry'
  sent: DraftContent; base: MailDraft | null; remote?: MailDraft; message: string
}
export function useMailDraftAutosave(options: Options) {
  const latest = useRef(options); latest.current = options
  const generation = useRef(0)
  const server = useRef<MailDraft | null>(options.draft)
  const confirmed = useRef(options.draft ? JSON.stringify(draftSnapshot(options.draft)) : null)
  const previous = useRef(''), localRevision = useRef(0)
  const currentSnapshot = JSON.stringify(draftSnapshot(options.content))
  if (previous.current !== currentSnapshot) { previous.current = currentSnapshot; localRevision.current++ }
  const [saving, setSaving] = useState(false), [failure, setFailure] = useState('')
  const [recovery, setRecovery] = useState<DraftRecovery | null>(null)
  const blocked = useRef(false)
  const flight = useRef<Promise<MailDraft> | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopTimer = () => { if (timer.current) clearTimeout(timer.current); timer.current = null }
  useEffect(() => {
    generation.current++; server.current = options.draft
    confirmed.current = options.draft ? JSON.stringify(draftSnapshot(options.draft)) : null
    flight.current = null; blocked.current = false; setFailure(''); setRecovery(null); setSaving(false)
    return () => { generation.current++; stopTimer() }
  }, [options.sessionId])
  useEffect(() => {
    if (!flight.current && options.draft) {
      server.current = options.draft
      // Loaded server metadata never replaces a newer local editor snapshot.
      confirmed.current = JSON.stringify(draftSnapshot(options.draft))
    }
  }, [options.draft])
  const verify = useCallback(async (record: DraftRecovery, context: number): Promise<MailDraft | null> => {
    if (!record.base) {
      setRecovery({ ...record, kind: 'post-unknown', message: 'Non \u00e8 possibile confermare il salvataggio. Verifica le bozze salvate.' })
      return null
    }
    try {
      const remote = await getMailDraft(record.base.id)
      if (context !== generation.current) return null
      if (record.kind !== 'conflict' && JSON.stringify(draftSnapshot(remote)) === JSON.stringify(record.sent)) {
        server.current = remote; confirmed.current = JSON.stringify(record.sent)
        blocked.current = false; setFailure(''); setRecovery(null); latest.current.onSaved(remote)
        if (!latest.current.hasBuffer && JSON.stringify(draftSnapshot(latest.current.content)) === confirmed.current) latest.current.onClean()
        return remote
      }
      if (remote.status === 'confirmed') latest.current.onSaved(remote)
      const unchanged = record.kind !== 'conflict' && remote.status === 'draft' && remote.version === record.base.version && JSON.stringify(draftSnapshot(remote)) === JSON.stringify(draftSnapshot(record.base))
      setRecovery({ ...record, remote, kind: unchanged ? 'retry' : 'conflict', message: unchanged ? 'La modifica non risulta salvata. Puoi riprovare esplicitamente.' : 'Confronta il tuo contenuto con la versione salvata.' })
    } catch (error) {
      if (context !== generation.current) return null
      if ([403, 404].includes((error as MailApiError).status)) {
        latest.current.onRevoked(); setRecovery(null); setFailure('Accesso revocato o risorsa non disponibile.')
      } else setRecovery({ ...record, kind: 'verify', message: 'Impossibile verificare il salvataggio. Riprova verifica.' })
    }
    return null
  }, [])
  const flush = useCallback(async (explicit?: DraftContent): Promise<MailDraft> => {
    stopTimer()
    const context = generation.current
    if (flight.current) {
      await flight.current
      if (context !== generation.current) throw new Error('Sessione cambiata.')
      return flush(explicit)
    }
    const state = latest.current
    const snapshot = draftSnapshot(explicit || state.content)
    const invalid = draftValidation(snapshot, !explicit && state.hasBuffer)
    if (!state.permitted || !state.mailbox || server.current?.status === 'confirmed') throw new Error('Salvataggio non autorizzato.')
    if (invalid) throw new Error(invalid)
    if (blocked.current) throw new Error('Salvataggio sospeso. Verifica la bozza prima di riprovare.')
    const encoded = JSON.stringify(snapshot)
    if (confirmed.current === encoded && server.current) return server.current
    const revision = localRevision.current
    const base = server.current
    setSaving(true); setFailure('')
    const work = (async () => {
      try {
        const saved = base ? await updateMailDraft(base.id, base.version, snapshot) : await createMailDraft(state.mailbox, snapshot)
        if (context !== generation.current) throw new Error('Sessione cambiata.')
        server.current = saved; confirmed.current = encoded
        latest.current.onSaved(saved)
        if (revision === localRevision.current && !latest.current.hasBuffer) latest.current.onClean()
        return saved
      } catch (error) {
        if (context === generation.current) {
          blocked.current = true
          setFailure((error as Error).message || 'Salvataggio non riuscito.')
          const status = (error as MailApiError).status
          if ([403, 404].includes(status)) latest.current.onRevoked()
          else if (!status || status >= 500 || status === 409) {
            const recovered = await verify({ kind: status === 409 ? 'conflict' : base ? 'verify' : 'post-unknown', base, sent: snapshot, message: '' }, context)
            if (recovered) return recovered
          }
        }
        throw error
      } finally {
        if (context === generation.current) { flight.current = null; setSaving(false) }
      }
    })()
    flight.current = work
    const saved = await work
    if (!explicit && context === generation.current) {
      const next = latest.current
      if (!next.paused && next.dirty && !draftValidation(next.content, next.hasBuffer) && JSON.stringify(draftSnapshot(next.content)) !== confirmed.current) return flush()
    }
    return saved
  }, [verify])
  useEffect(() => {
    stopTimer()
    if (options.dirty && options.permitted && !options.paused && !failure && !saving && !draftValidation(options.content, options.hasBuffer) && currentSnapshot !== confirmed.current) {
      timer.current = setTimeout(() => { if (server.current?.status !== 'confirmed') void flush().catch(() => {}) }, 1000)
    }
    return stopTimer
  }, [currentSnapshot, options.hasBuffer, options.dirty, options.permitted, options.paused, failure, saving, flush])
  const status = failure || (saving ? 'Salvataggio\u2026' : options.dirty ? draftValidation(options.content, options.hasBuffer) || 'Modifiche da salvare' : confirmed.current === currentSnapshot && !options.hasBuffer ? 'Salvato' : '')
  const retryVerification = async () => { if (recovery) await verify(recovery, generation.current) }
  const loadRemote = () => {
    if (!recovery?.remote) return
    server.current = recovery.remote; confirmed.current = JSON.stringify(draftSnapshot(recovery.remote))
    latest.current.onReload(recovery.remote); blocked.current = false; setRecovery(null); setFailure('')
  }
  const keepLocal = async () => {
    if (!recovery?.remote || recovery.remote.status !== 'draft') return
    server.current = recovery.remote; confirmed.current = JSON.stringify(draftSnapshot(recovery.remote))
    latest.current.onSaved(recovery.remote)
    blocked.current = false; setRecovery(null); setFailure('')
    await flush()
  }
  const createDistinct = async () => {
    if (recovery?.kind !== 'post-unknown') return
    server.current = null; confirmed.current = null; blocked.current = false; setRecovery(null); setFailure('')
    await flush()
  }
  const compareDraft = async (id: string) => {
    if (recovery?.kind !== 'post-unknown') return
    const context = generation.current
    try {
      const remote = await getMailDraft(id)
      if (context === generation.current) setRecovery({ ...recovery, kind: 'conflict', remote, message: 'Confronta la bozza selezionata prima di associarla alla composizione.' })
    } catch (error) { if (context === generation.current) setFailure((error as Error).message) }
  }
  return { flush, saving, status, failure, recovery, retryVerification, loadRemote, keepLocal, createDistinct, compareDraft, localRevision: localRevision.current, stopTimer }
}
